import {
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js'
import jwt from 'jsonwebtoken'
import moment from 'moment'
import Raygun from './Raygun'
import Store from '../store/Store'
import axios from '../lib/axios'
import CryptoJS from 'crypto-js'
const url = process.env.ZLICK_V2
const SECRET_KEY = 'Zlick-TwoFA-Secret-Key-p1nc0d3'

export default {
  async login ({ email, password }) {
    const promise = await new Promise((resolve, reject) => {
      const cognitoUser = getCognitoUser(email)
      const authenticationDetails = getAuthenitacationDetails({ email, password })
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (result) => {
          setResultToLocalStorage(result)
          await this.processPinCodeAuthentication(result, email, password)
          resolve('success')
        },
        onFailure: (error) => {
          reject(error)
          Raygun.send(error)
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          delete userAttributes.email_verified
          resolve('newPasswordRequired')
        }
      })
    })
    return promise
  },
  async changePassword ({ newPassword, currentPassword }) {
    const promise = await new Promise((resolve, reject) => {
      const cognitoUser = getUserPool().getCurrentUser()
      // user data not available
      if (!cognitoUser) return

      const AccessToken = new CognitoAccessToken({ AccessToken: localStorage.getItem('access_token') })
      const IdToken = new CognitoIdToken({ IdToken: localStorage.getItem('token') })
      const RefreshToken = new CognitoRefreshToken({ RefreshToken: localStorage.getItem('refresh_token') })

      const sessionData = {
        IdToken: IdToken,
        AccessToken: AccessToken,
        RefreshToken: RefreshToken
      }

      const userSession = new CognitoUserSession(sessionData)
      cognitoUser.setSignInUserSession(userSession)
      cognitoUser.changePassword(currentPassword, newPassword, (err, data) => {
        if (err) {
          reject(err)
          Raygun.send(err)
        } else {
          resolve('success')
        }
      })
    })
    return promise
  },

  signOut () {
    const cognitoUser = getUserPool().getCurrentUser()
    cognitoUser.signOut()
    localStorage.clear()
  },

  clearLoginState () {
    localStorage.clear()
    localStorage.removeItem('vuex')
  },

  async completeRegistration ({ userEmail, userFirstName, userLastName, currentPassword, newPassword }) {
    const promise = await new Promise((resolve, reject) => {
      const newUserAttributes = {
        name: userFirstName,
        family_name: userLastName,
        email: userEmail
      }
      const authenticationDetails = getAuthenitacationDetails({ email: userEmail, password: currentPassword })
      const cognitoUser = getCognitoUser(userEmail)

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          setResultToLocalStorage(result)
          resolve('success')
        },
        onFailure: function (error) {
          reject(error)
          Raygun.send(error)
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          delete userAttributes.email_verified
          cognitoUser.completeNewPasswordChallenge(newPassword, newUserAttributes, this)
        }
      })
    })
    return promise
  },

  refreshSession () {
    return new Promise((resolve, reject) => {
      // try to refresh the session if user is not logged in
      const cognitoUser = getUserPool().getCurrentUser()
      // user data not available
      if (!cognitoUser) return

      const AccessToken = new CognitoAccessToken({ AccessToken: localStorage.getItem('access_token') })
      const IdToken = new CognitoIdToken({ IdToken: localStorage.getItem('token') })
      const RefreshToken = new CognitoRefreshToken({ RefreshToken: localStorage.getItem('refresh_token') })

      const sessionData = {
        IdToken: IdToken,
        AccessToken: AccessToken,
        RefreshToken: RefreshToken
      }

      const userSession = new CognitoUserSession(sessionData)
      cognitoUser.setSignInUserSession(userSession)

      // Refresh hapens here if necessary
      // Get a session from the session object or local storage or by using a refresh token
      cognitoUser.getSession((err, session) => {
        if (err) {
          return reject(err)
        }
        if (session.isValid()) {
          setResultToLocalStorage(session)
          return resolve(session)
        }
      })
    })
  },

  isRoleEmpty () {
    if (!localStorage.getItem('role')) {
      return true
    }
    const permittedRoles = localStorage.getItem('role').split(' ')
    if (permittedRoles.length === 0) {
      return true
    }
    return false
  },

  isInRole (roles) {
    let permittedRoles = []
    if (localStorage.getItem('role')) {
      permittedRoles = localStorage.getItem('role').split(' ')
    }
    return roles.filter(x => permittedRoles.indexOf(x) > -1).length > 0
  },

  isAnalyst () {
    let permittedRoles = []
    if (localStorage.getItem('role')) {
      permittedRoles = localStorage.getItem('role').split(' ')
    }
    if ((permittedRoles.includes('analyst'))) {
      if ((permittedRoles.includes('admin')) || (permittedRoles.includes('support'))) {
        return false
      } else {
        return true
      }
    } else {
      return false
    }
  },

  isUserLoggedIn (expiryWindow = 15) {
    var token = localStorage.getItem('token')
    if (!token) return false
    const payload = jwt.decode(token)
    return moment.unix(payload.exp) > moment().add(expiryWindow, 'seconds')
  },
  async setApiClient () {
    const apiclient = await getApiClient()
    localStorage.setItem('apiClient', JSON.stringify(apiclient.data.apiClient))
    var apiClientObject = JSON.parse(localStorage.getItem('apiClient'))
    let isTwoFAEnabled = apiClientObject.portalTwoFAEnabled
    localStorage.setItem('isTwoFAEnabled', isTwoFAEnabled)
    return { isTwoFAEnabled, apiClientObject }
  },

  getPinCodeDetails (result) {
    let pinCode, pincodeTime, pincodeUsageCount
    if (result.idToken.payload['custom:pinCode']) {
      pinCode = result.idToken.payload['custom:pinCode']
    }
    if (result.idToken.payload['custom:pincodeTime']) {
      pincodeTime = result.idToken.payload['custom:pincodeTime']
    }
    if (result.idToken.payload['custom:pincodeUsageCount']) {
      pincodeUsageCount = result.idToken.payload['custom:pincodeUsageCount']
    }
    return { pinCode, pincodeTime, pincodeUsageCount }
  },

  async handlePinCode ({ pinCode, pincodeTime, pincodeUsageCount, email, password }) {
    if (!pinCode) {
      let { pinCode } = sendPincode(email)
      const pincodeTime = new Date().getTime()
      await updateOtpAndOtpTime(email, pinCode, pincodeTime, 0)
      await getUserAttributes(email, password)
    }
    if (pinCode) {
      // The expression 24 * 60 * 60 * 1000 is used to calculate the number of milliseconds in a day.
      const twentyFourHourMilliSeconds = 24 * 60 * 60 * 1000
      const pinAge = Date.now() - (pincodeTime)
      if (pinAge > twentyFourHourMilliSeconds || pincodeUsageCount > 0) {
        const pincodeUsageCount = 0
        const pincodeTime = new Date().getTime()
        let { pinCode } = sendPincode(email)
        await updateOtpAndOtpTime(email, pinCode, pincodeTime, pincodeUsageCount)
        await getUserAttributes(email, password)
      }
    }
  },

  async processPinCodeAuthentication (result, email, password) {
    const { isTwoFAEnabled } = await this.setApiClient()
    if (isTwoFAEnabled) {
      const pinCodeDetails = this.getPinCodeDetails(result)
      await this.handlePinCode({ ...pinCodeDetails, email, password })
    }
  },
  decryptProperty (property) {
    const encryptedProperty = localStorage.getItem(property)
    if (encryptedProperty) {
      const bytes = CryptoJS.AES.decrypt(encryptedProperty, SECRET_KEY)
      const originalProperty = bytes.toString(CryptoJS.enc.Utf8)
      return originalProperty
    }
  }
}

function sendPincode (email) {
  const pinCode = Math.floor(1000 + Math.random() * 9000)
  try {
    var response = axios({
      method: 'POST',
      url: url + '/sendEmail',
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('access_token'),
        'zlick-group': Store.getters.getActiveCognitoGroup
      },
      data: {
        email: email,
        pinCode: pinCode
      }
    })
      .catch((error) => {
        throw new Error('Failed to Send Email' + error)
      })
    return { response, pinCode }
  } catch (error) {
    throw new Error('Failed to Send Email' + error)
  }
}

async function getApiClient () {
  try {
    var response = axios({
      method: 'GET',
      url: url + '/getApiClient',
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('access_token'),
        'zlick-group': Store.getters.getActiveCognitoGroup
      }
    })
      .catch((error) => {
        throw new Error('Failed to get apiClient' + error)
      })
    return response
  } catch (error) {
    throw new Error('Failed to get apiClient' + error)
  }
}
async function updateOtpAndOtpTime (email, pinCode, pincodeTime, pincodeUsageCount) {
  try {
    var response = axios.get(url + '/updateUser', {
      params: {
        Username: email,
        pinCode: pinCode,
        pincodeTime: pincodeTime,
        pincodeUsageCount: pincodeUsageCount

      },
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'zlick-group': Store.getters.getActiveCognitoGroup
      }
    })
      .catch((error) => {
        throw new Error('Failed to update updateOtpAndOtpTime ' + error)
      })
    return response
  } catch (err) {
    console.warn('Failed to update updateOtpAndOtpTime' + err)
  }
}

async function getUserAttributes (email, password) {
  const cognitoUser = getCognitoUser(email)
  const authenticationDetails = getAuthenitacationDetails({ email, password })
  cognitoUser.authenticateUser(authenticationDetails, {
    onSuccess: (result) => {
      if (result.idToken.payload['custom:pinCode']) {
        const encryptedPinCode = CryptoJS.AES.encrypt(result.idToken.payload['custom:pinCode'], SECRET_KEY).toString()
        localStorage.setItem('cognitoPinCode', encryptedPinCode)
      }
      if (result.idToken.payload['custom:pincodeTime']) {
        const encryptedPinCodeTime = CryptoJS.AES.encrypt(result.idToken.payload['custom:pincodeTime'], SECRET_KEY).toString()
        localStorage.setItem('cognitoPinCodeTime', encryptedPinCodeTime)
      }
      if (result.idToken.payload['custom:pincodeUsageCount']) {
        const encryptedPinCodeUsageCount = CryptoJS.AES.encrypt(result.idToken.payload['custom:pincodeUsageCount'], SECRET_KEY).toString()
        localStorage.setItem('pincodeUsageCount', encryptedPinCodeUsageCount)
      }
    },
    onFailure: (error) => {
      console.log('error', error)
    }
  })
}

function getCognitoUser (email) {
  const userData = {
    Username: email,
    Pool: getUserPool()
  }
  return new CognitoUser(userData)
}

function getUserPool () {
  const poolData = {
    UserPoolId: process.env.COGNITO_UP_ID,
    ClientId: process.env.COGNITO_CLIENT_ID
  }
  return new CognitoUserPool(poolData)
}

function getAuthenitacationDetails ({ email, password }) {
  const authenticationData = {
    email: email,
    Password: password
  }
  return new AuthenticationDetails(authenticationData)
}

function setResultToLocalStorage (result) {
  localStorage.setItem('email', result.getIdToken().payload.email)
  localStorage.setItem('name', result.getIdToken().payload.name)
  localStorage.setItem('family_name', result.getIdToken().payload.familiy_name)
  localStorage.setItem('token', result.getIdToken().getJwtToken())
  localStorage.setItem('access_token', result.getAccessToken().getJwtToken())
  localStorage.setItem('refresh_token', result.getRefreshToken().getToken())
  if (result.idToken.payload['custom:role']) {
    localStorage.setItem('role', result.idToken.payload['custom:role'])
  }
  if (result.idToken.payload['custom:pinCode']) {
    const encryptedPinCode = CryptoJS.AES.encrypt(result.idToken.payload['custom:pinCode'], SECRET_KEY).toString()
    localStorage.setItem('cognitoPinCode', encryptedPinCode)
  }
  if (result.idToken.payload['custom:pincodeTime']) {
    const encryptedPinCodeTime = CryptoJS.AES.encrypt(result.idToken.payload['custom:pincodeTime'], SECRET_KEY).toString()
    localStorage.setItem('cognitoPinCodeTime', encryptedPinCodeTime)
  }
  if (result.idToken.payload['custom:pincodeUsageCount']) {
    const encryptedPinCodeUsageCount = CryptoJS.AES.encrypt(result.idToken.payload['custom:pincodeUsageCount'], SECRET_KEY).toString()
    localStorage.setItem('pincodeUsageCount', encryptedPinCodeUsageCount)
  }

  window.Store = Store

  const cognitoGroups = result.idToken.payload['cognito:groups']
  if (!Store.getters.getActiveCognitoGroup) {
    Store.commit('setActiveCognitoGroup', cognitoGroups && cognitoGroups[0])
  }

  Store.commit('setAllowedCognitoGroups', cognitoGroups || [])
  if (result.idToken.payload['custom:role']) {
    Store.commit('setUserRoles', result.idToken.payload['custom:role'].split(' '))
  }
}



// WEBPACK FOOTER //
// ./src/services/AuthenticationService.js